import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { FolderTree, Plus, Edit, Trash2, Loader2, Lock, DollarSign, TrendingUp, TrendingDown, Landmark, BookOpen, ChevronDown, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { JournalEntryDialog } from '@/components/JournalEntryDialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

// Sortable table header component
const SortableHeader = ({ column, label, sortConfig, onSort, className = '' }) => {
  const isActive = sortConfig.key === column;
  return (
    <TableHead 
      className={`font-semibold cursor-pointer select-none hover:bg-slate-100 transition-colors ${className}`}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive ? (
          sortConfig.direction === 'asc' ? 
            <ArrowUp className="w-3.5 h-3.5 text-slate-700" /> : 
            <ArrowDown className="w-3.5 h-3.5 text-slate-700" />
        ) : (
          <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
        )}
      </div>
    </TableHead>
  );
};

import { API } from '../utils/api';

const Categories = () => {
  const { getAuthHeaders, user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [llcs, setLLCs] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editingJournalEntry, setEditingJournalEntry] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showAllJournalEntries, setShowAllJournalEntries] = useState(false);
  const [journalEntriesOpen, setJournalEntriesOpen] = useState(() => {
    const saved = localStorage.getItem('categoriesPage_journalEntriesOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [categoriesOpen, setCategoriesOpen] = useState(() => {
    const saved = localStorage.getItem('categoriesPage_categoriesOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [formData, setFormData] = useState({ name: '', category_type: 'expense', description: '' });
  
  // Sorting state for Journal Entries table
  const [journalSort, setJournalSort] = useState({ key: 'date', direction: 'desc' });
  // Sorting state for Categories table
  const [categorySort, setCategorySort] = useState({ key: 'name', direction: 'asc' });

  // Sort handler for Journal Entries
  const handleJournalSort = (key) => {
    setJournalSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort handler for Categories
  const handleCategorySort = (key) => {
    setCategorySort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort journal entries
  const getSortedJournalEntries = () => {
    return [...journalEntries].sort((a, b) => {
      let aVal, bVal;
      if (journalSort.key === 'llc') {
        aVal = llcs.find(l => l.id === a.llc_id)?.name || '';
        bVal = llcs.find(l => l.id === b.llc_id)?.name || '';
      } else {
        aVal = a[journalSort.key] || '';
        bVal = b[journalSort.key] || '';
      }
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return journalSort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return journalSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Sort categories
  const getSortedCategories = (cats) => {
    return [...cats].sort((a, b) => {
      let aVal, bVal;
      if (categorySort.key === 'status') {
        aVal = a.is_system ? 'system' : 'custom';
        bVal = b.is_system ? 'system' : 'custom';
      } else if (categorySort.key === 'type') {
        aVal = categoryTypeLabels[a.category_type] || a.category_type;
        bVal = categoryTypeLabels[b.category_type] || b.category_type;
      } else {
        aVal = a[categorySort.key] || '';
        bVal = b[categorySort.key] || '';
      }
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return categorySort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return categorySort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Persist collapse state to localStorage
  const handleJournalEntriesToggle = (open) => {
    setJournalEntriesOpen(open);
    localStorage.setItem('categoriesPage_journalEntriesOpen', JSON.stringify(open));
  };

  const handleCategoriesToggle = (open) => {
    setCategoriesOpen(open);
    localStorage.setItem('categoriesPage_categoriesOpen', JSON.stringify(open));
  };

  const fetchData = async () => {
    try {
      await axios.post(`${API}/categories/seed`, {}, { headers: getAuthHeaders() }).catch(() => {});
      const [catResponse, llcResponse, jeResponse] = await Promise.all([
        axios.get(`${API}/categories`, { headers: getAuthHeaders() }),
        axios.get(`${API}/llcs`, { headers: getAuthHeaders() }),
        axios.get(`${API}/journal-entries`, { headers: getAuthHeaders() })
      ]);
      setCategories(catResponse.data);
      setLLCs(llcResponse.data);
      setJournalEntries(jeResponse.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (selectedCategory) {
        await axios.put(`${API}/categories/${selectedCategory.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/categories`, formData, { headers: getAuthHeaders() });
        toast.success('Category created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (category) => {
    if (category.is_system) { toast.error('System categories cannot be edited'); return; }
    setSelectedCategory(category);
    setFormData({ name: category.name, category_type: category.category_type, description: category.description || '' });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;
    try {
      await axios.delete(`${API}/categories/${selectedCategory.id}`, { headers: getAuthHeaders() });
      toast.success('Category deleted');
      setDeleteDialogOpen(false);
      setSelectedCategory(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const resetForm = () => {
    setSelectedCategory(null);
    setFormData({ name: '', category_type: 'expense', description: '' });
  };

  // Category type display names
  const categoryTypeLabels = {
    current_asset: 'Current Asset',
    fixed_asset: 'Fixed Asset',
    other_asset: 'Other Asset',
    current_liability: 'Current Liability',
    long_term_liability: 'Long-term Liability',
    equity: 'Equity',
    income: 'Income',
    expense: 'Expense'
  };

  const getCategoryIcon = (type) => {
    const icons = { 
      current_asset: <DollarSign className="w-4 h-4" />, 
      fixed_asset: <Landmark className="w-4 h-4" />, 
      other_asset: <FolderTree className="w-4 h-4" />,
      current_liability: <TrendingDown className="w-4 h-4" />, 
      long_term_liability: <TrendingDown className="w-4 h-4" />,
      equity: <Landmark className="w-4 h-4" />,
      income: <TrendingUp className="w-4 h-4" />, 
      expense: <TrendingDown className="w-4 h-4" /> 
    };
    return icons[type] || <FolderTree className="w-4 h-4" />;
  };

  const getCategoryColor = (type) => {
    const colors = { 
      current_asset: 'bg-blue-100 text-blue-800 border-blue-200', 
      fixed_asset: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      other_asset: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      current_liability: 'bg-amber-100 text-amber-800 border-amber-200', 
      long_term_liability: 'bg-orange-100 text-orange-800 border-orange-200',
      equity: 'bg-purple-100 text-purple-800 border-purple-200',
      income: 'bg-emerald-100 text-emerald-800 border-emerald-200', 
      expense: 'bg-rose-100 text-rose-800 border-rose-200' 
    };
    return colors[type] || 'bg-slate-100 text-slate-800 border-slate-200';
  };

  // Group categories by balance sheet vs income statement
  const assetTypes = ['current_asset', 'fixed_asset', 'other_asset'];
  const liabilityTypes = ['current_liability', 'long_term_liability'];
  
  const filteredCategories = activeTab === 'all' ? categories 
    : activeTab === 'assets' ? categories.filter(c => assetTypes.includes(c.category_type))
    : activeTab === 'liabilities' ? categories.filter(c => liabilityTypes.includes(c.category_type))
    : categories.filter(c => c.category_type === activeTab);
  
  const categoryCounts = { 
    all: categories.length, 
    assets: categories.filter(c => assetTypes.includes(c.category_type)).length, 
    liabilities: categories.filter(c => liabilityTypes.includes(c.category_type)).length, 
    equity: categories.filter(c => c.category_type === 'equity').length,
    income: categories.filter(c => c.category_type === 'income').length, 
    expense: categories.filter(c => c.category_type === 'expense').length 
  };
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const canEdit = user?.role === 'superadmin' || user?.role === 'manager';

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-amber-700" /></div>;

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900">Account Categories</h1>
          <p className="text-slate-500 mt-1">Manage categories for transactions and financial reporting</p>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button variant="outline" onClick={() => { setEditingJournalEntry(null); setJournalDialogOpen(true); }} data-testid="journal-entry-btn">
              <BookOpen className="w-4 h-4 mr-2" />Journal Entry
            </Button>
          )}
          {isAdmin && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-category-btn"><Plus className="w-4 h-4 mr-2" />Add Category</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">{selectedCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
                  <DialogDescription>{selectedCategory ? 'Update category information' : 'Create a custom account category'}</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Category Name *</Label>
                      <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Parking Revenue" required />
                    </div>
                    <div className="space-y-2">
                      <Label>Category Type *</Label>
                      <Select value={formData.category_type} onValueChange={(value) => setFormData({ ...formData, category_type: value })}>
                        <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="current_asset">Current Asset</SelectItem>
                          <SelectItem value="fixed_asset">Fixed Asset</SelectItem>
                          <SelectItem value="other_asset">Other Asset</SelectItem>
                          <SelectItem value="current_liability">Current Liability</SelectItem>
                          <SelectItem value="long_term_liability">Long-term Liability</SelectItem>
                          <SelectItem value="equity">Equity</SelectItem>
                          <SelectItem value="income">Income</SelectItem>
                          <SelectItem value="expense">Expense</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description..." rows={3} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                    <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={formLoading}>
                      {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (selectedCategory ? 'Update' : 'Create')}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <JournalEntryDialog 
        open={journalDialogOpen} 
        onOpenChange={setJournalDialogOpen} 
        getAuthHeaders={getAuthHeaders} 
        categories={categories} 
        llcs={llcs} 
        onCategoryCreated={(cat) => setCategories([...categories, cat])}
        editEntry={editingJournalEntry}
        onSaved={fetchData}
      />

      {/* Journal Entries Section */}
      {journalEntries.length > 0 && (
        <Collapsible open={journalEntriesOpen} onOpenChange={handleJournalEntriesToggle}>
          <Card className="border-slate-200">
            <CollapsibleTrigger asChild>
              <CardHeader className="pb-3 cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {journalEntriesOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                    <BookOpen className="w-5 h-5 text-slate-600" />
                    <CardTitle className="font-serif text-lg">Journal Entries</CardTitle>
                  </div>
                  <span className="text-sm text-slate-500">{journalEntries.length} entries</span>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-900 hover:bg-slate-900">
                      <SortableHeader column="date" label="Date" sortConfig={journalSort} onSort={handleJournalSort} className="text-white hover:bg-slate-800" />
                      <SortableHeader column="llc" label="LLC" sortConfig={journalSort} onSort={handleJournalSort} className="text-white hover:bg-slate-800" />
                      <SortableHeader column="description" label="Description" sortConfig={journalSort} onSort={handleJournalSort} className="text-white hover:bg-slate-800" />
                      <TableHead className="font-semibold text-white">Lines</TableHead>
                      <SortableHeader column="reference" label="Reference" sortConfig={journalSort} onSort={handleJournalSort} className="text-white hover:bg-slate-800" />
                      {canEdit && <TableHead className="w-[60px] text-white"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(showAllJournalEntries ? getSortedJournalEntries() : getSortedJournalEntries().slice(0, 10)).map((entry) => {
                      const llc = llcs.find(l => l.id === entry.llc_id);
                      return (
                        <TableRow key={entry.id} className="cursor-pointer hover:bg-slate-50" onClick={() => { if (canEdit) { setEditingJournalEntry(entry); setJournalDialogOpen(true); } }}>
                          <TableCell className="font-mono text-sm">{entry.date}</TableCell>
                          <TableCell>{llc?.name || '-'}</TableCell>
                          <TableCell className="font-medium">{entry.description}</TableCell>
                          <TableCell>
                            <span className="text-xs text-slate-600">
                              {entry.lines?.map(l => l.category_name).join(', ').slice(0, 40)}{entry.lines?.length > 2 ? '...' : ''}
                            </span>
                          </TableCell>
                          <TableCell className="text-slate-500">{entry.reference || '-'}</TableCell>
                          {canEdit && (
                            <TableCell>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setEditingJournalEntry(entry); setJournalDialogOpen(true); }}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {journalEntries.length > 10 && (
                  <div className="flex items-center justify-center gap-2 mt-3">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowAllJournalEntries(!showAllJournalEntries)}
                    >
                      {showAllJournalEntries ? 'Show Less' : `Show All ${journalEntries.length} Entries`}
                    </Button>
                    {!showAllJournalEntries && (
                      <span className="text-sm text-slate-500">Showing 10 of {journalEntries.length}</span>
                    )}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      <Collapsible open={categoriesOpen} onOpenChange={handleCategoriesToggle}>
        <Card className="border-slate-200">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {categoriesOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <FolderTree className="w-5 h-5 text-slate-600" />
                  <CardTitle className="font-serif text-lg">All Categories</CardTitle>
                </div>
                <span className="text-sm text-slate-500">{categories.length} categories</span>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-6">
                  <TabsTrigger value="all">All ({categoryCounts.all})</TabsTrigger>
                  <TabsTrigger value="assets">Assets ({categoryCounts.assets})</TabsTrigger>
                  <TabsTrigger value="liabilities">Liabilities ({categoryCounts.liabilities})</TabsTrigger>
                  <TabsTrigger value="equity">Equity ({categoryCounts.equity})</TabsTrigger>
                  <TabsTrigger value="income">Income ({categoryCounts.income})</TabsTrigger>
                  <TabsTrigger value="expense">Expense ({categoryCounts.expense})</TabsTrigger>
                </TabsList>

                <TabsContent value={activeTab} className="mt-6">
                  {filteredCategories.length === 0 ? (
                    <div className="text-center py-12">
                      <FolderTree className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <h3 className="font-serif text-lg font-semibold text-slate-700">No categories found</h3>
                      <p className="text-slate-500 mt-1">Create your first custom category</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-900 hover:bg-slate-900">
                          <SortableHeader column="name" label="Name" sortConfig={categorySort} onSort={handleCategorySort} className="text-white hover:bg-slate-800" />
                          <SortableHeader column="type" label="Type" sortConfig={categorySort} onSort={handleCategorySort} className="text-white hover:bg-slate-800" />
                          <SortableHeader column="description" label="Description" sortConfig={categorySort} onSort={handleCategorySort} className="text-white hover:bg-slate-800" />
                          <SortableHeader column="status" label="Status" sortConfig={categorySort} onSort={handleCategorySort} className="text-white hover:bg-slate-800" />
                          {isAdmin && <TableHead className="w-[80px] text-white"></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getSortedCategories(filteredCategories).map((category) => (
                          <TableRow key={category.id}>
                            <TableCell className="font-medium">
                              <span className="flex items-center gap-2">{getCategoryIcon(category.category_type)}{category.name}</span>
                            </TableCell>
                            <TableCell><Badge variant="outline" className={getCategoryColor(category.category_type)}>{categoryTypeLabels[category.category_type] || category.category_type}</Badge></TableCell>
                            <TableCell className="text-slate-500 max-w-[300px] truncate">{category.description || '-'}</TableCell>
                            <TableCell>{category.is_system ? <Badge variant="outline" className="bg-slate-50"><Lock className="w-3 h-3 mr-1" />System</Badge> : <Badge variant="outline" className="bg-emerald-50 text-emerald-700">Custom</Badge>}</TableCell>
                            {isAdmin && (
                              <TableCell>
                                {!category.is_system && (
                                  <div className="flex gap-1 justify-end">
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(category)}><Edit className="w-4 h-4" /></Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600 hover:text-rose-700" onClick={() => { setSelectedCategory(category); setDeleteDialogOpen(true); }}><Trash2 className="w-4 h-4" /></Button>
                                  </div>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete &quot;{selectedCategory?.name}&quot;? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Categories;
